import { MoreOutlined } from "@ant-design/icons";
import React, { useContext, useState } from "react";
import "./NotificationsScreen.css";
import NavigationBar from "../../components/element/NavigationBar";
import FBOHeaderBar from "../../components/element/FBOHeaderBar";
import useFBOApi from "../../services/api/useFBOApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Loader from "../../components/element/Loader";
import ROUTE_PATHS from "../../utils/routePaths";
import { Link } from "react-router-dom";
import { StorageContext } from "../../services/StorageProvider";
import { PAGINATION_PAGE_SIZE, NOTIFICATION_FILTERS } from "../../utils/constants";
import SnackbarAlert from "../../components/element/SnackbarAlert";

const NotificationsScreen = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = PAGINATION_PAGE_SIZE;
  const {
    fetchAllNotifications,
    readNotifications,
    unreadNotification,
    deleteNotification,
    readAllNotifications,
  } = useFBOApi();
  const { fboUserProfileData } = useContext(StorageContext);
  const [filter, setFilter] = useState(NOTIFICATION_FILTERS.ALL);
  const [activeMenu, setActiveMenu] = useState(null);
  const [actionCount, setActionCount] = useState(0);
  const queryClient = useQueryClient();
  const [snackbarKey, setSnackbarKey] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    data: notificationsData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["fbo-notifications-list", currentPage, actionCount],
    queryFn: () =>
      fetchAllNotifications({
        page: currentPage,
        size: pageSize,
      }),
    enabled: !!fboUserProfileData,
    retry: false,
  });

  const readNotificationMutation = useMutation({
    mutationFn: ({ notificationId, data }) =>
      readNotifications(notificationId, data),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setActiveMenu(null);
    },
  });

  const unreadNotificationMutation = useMutation({
    mutationFn: ({ notificationId, data }) =>
      unreadNotification(notificationId, data),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setActiveMenu(null);
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: ({ notificationId, data }) =>
      deleteNotification(notificationId, data),
    onSuccess: () => {
      setActionCount(actionCount + 1);
      setActiveMenu(null);
    },
  });

  const readAllNotificationsMutation = useMutation({
    mutationFn: () => readAllNotifications(),
    onSuccess: () => {
      setShowSuccessMessage(true);
      setErrorMessage("");
      setSnackbarKey((prev) => prev + 1);
      setActionCount((prev) => prev + 1);
      setActiveMenu(null);

      // Refresh unread badge in the navigation bar
      queryClient.invalidateQueries({ queryKey: ["fbo-notifications-count"] });
    },
    onError: (error) => {
      setShowSuccessMessage(false);
      setErrorMessage(
        error?.message || "Failed to mark all notifications as read"
      );
      setSnackbarKey((prev) => prev + 1);
    },
    retry: false,
  });

  const getFilteredNotifications = () => {
    if (filter === NOTIFICATION_FILTERS.ALL) return notificationsData?.data?.content;
    if (filter === NOTIFICATION_FILTERS.READ)
      return notificationsData?.data?.content?.filter(
        (notification) => notification.isRead
      );
    if (filter === NOTIFICATION_FILTERS.UNREAD)
      return notificationsData?.data?.content?.filter(
        (notification) => !notification.isRead
      );
    return notificationsData?.data?.content;
  };

  const toggleMenu = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };

  const handleFilterChange = (filterType) => {
    if (filter === filterType) {
      setFilter("all");
    } else {
      setFilter(filterType);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    readNotificationMutation.mutate({
      notificationId: notificationId,
      data: {
        profileId: fboUserProfileData?.id,
      },
    });
  };

  const handleMarkAsUnread = async (notificationId) => {
    unreadNotificationMutation.mutate({
      notificationId: notificationId,
      data: {
        profileId: fboUserProfileData?.id,
      },
    });
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      readNotificationMutation.mutate({
        notificationId: notification?.id,
        data: {
          profileId: fboUserProfileData?.id,
        },
      });
    }
  };

  const handleDelete = async (notificationId) => {
    deleteNotificationMutation.mutate({
      notificationId: notificationId,
      data: {
        profileId: fboUserProfileData?.id,
      },
    });
  };

  const handleMarkAllAsRead = () => {
    setShowSuccessMessage(false);
    setErrorMessage("");
    readAllNotificationsMutation.mutate();
  };

  const handleNextPage = () => {
    if (currentPage + 1 < notificationsData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
      setActiveMenu(null);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      setActiveMenu(null);
    }
  };

  const getLoadingStatus = () => {
    return (
      isLoading ||
      readNotificationMutation?.isPending ||
      unreadNotificationMutation?.isPending ||
      deleteNotificationMutation?.isPending ||
      readAllNotificationsMutation?.isPending
    );
  };

  return (
    <div>
      <NavigationBar />
      <FBOHeaderBar
        title={"Notifications"}
        backContent={
          <div className="navigation-header">
            <Link to={ROUTE_PATHS.FBO_HOME} className="navigation-header-link">
              Home/
            </Link>
            Notifications
          </div>
        }
      />
      <div className="notification-screen-filters">
        <div className="notification-screen-filter-options">
          <label className="notification-screen-checkbox-container">
            <input
              type="radio"
              name="filter"
              checked={filter === NOTIFICATION_FILTERS.ALL}
              onChange={() => handleFilterChange("all")}
            />
            All
          </label>
          <label className="notification-screen-checkbox-container">
            <input
              type="radio"
              name="filter"
              checked={filter === NOTIFICATION_FILTERS.READ}
              onChange={() => handleFilterChange("read")}
            />
            Read
          </label>
          <label className="notification-screen-checkbox-container">
            <input
              type="radio"
              name="filter"
              checked={filter === NOTIFICATION_FILTERS.UNREAD}
              onChange={() => handleFilterChange("unread")}
            />
            Unread
          </label>
          <button
            className="notification-screen-mark-all-button"
            onClick={handleMarkAllAsRead}
            disabled={readAllNotificationsMutation.isPending || isLoading}
          >
            Mark All as Read
          </button>
        </div>
      </div>

      {showSuccessMessage && (
        <SnackbarAlert
          key={`success-${snackbarKey}`}
          type="success"
          onClose={() => setShowSuccessMessage(false)}
        >
          All notifications marked as read.
        </SnackbarAlert>
      )}
      {errorMessage && (
        <SnackbarAlert
          key={`error-${snackbarKey}`}
          type="error"
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </SnackbarAlert>
      )}

      <div className="notification-screen-list">
        {getLoadingStatus() ? (
          <Loader />
        ) : error && error.status === 404 ? (
          <div className="notification-screen-no-data">
            No notifications founds
          </div>
        ) : notificationsData?.data && notificationsData?.data?.length === 0 ? (
          <div className="notification-screen-no-data">
            No notifications found
          </div>
        ) : (
          <div>
            <div>
              {getFilteredNotifications()?.map((item, index) => (
                <div
                  key={item?.id}
                  className={`notification-screen-item ${
                    item?.isRead ? "" : "notification-unread"
                  }`}
                >
                  <div
                    className="notification-screen-content"
                    onClick={() => handleNotificationClick(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="notification-message">
                      {item?.notification}
                    </div>
                    <div className="notification-timestamp">
                      {dayjs(item?.createdAt).format("MMM D, YYYY HH:mm")}
                    </div>
                  </div>
                  <div className="notification-screen-actions">
                    <div className="notification-screen-menu">
                      <button
                        className="notification-screen-menu-button"
                        onClick={() => toggleMenu(index)}
                      >
                        <MoreOutlined rotate={90} />
                      </button>
                      {activeMenu === index && (
                        <div className="notification-screen-dropdown">
                          {!item?.isRead && (
                            <div
                              className="notification-screen-menu-item"
                              onClick={() => handleMarkAsRead(item?.id)}
                            >
                              Mark as Read
                            </div>
                          )}
                          {item?.isRead && (
                            <div
                              className="notification-screen-menu-item"
                              onClick={() => handleMarkAsUnread(item?.id)}
                            >
                              Mark as Unread
                            </div>
                          )}
                          <div
                            className="notification-screen-menu-item"
                            onClick={() => handleDelete(item?.id)}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {notificationsData?.data?.content &&
        notificationsData?.data?.content.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-content">
              Page {currentPage + 1} of {notificationsData?.data?.totalPages}
            </div>
            <button
              className="pagination-button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <CaretLeftOutlined />
            </button>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={currentPage + 1 === notificationsData?.data?.totalPages}
            >
              <CaretRightOutlined />
            </button>
          </div>
        )}
    </div>
  );
};

export default NotificationsScreen;
